<script setup>
import { ref, computed, onMounted } from 'vue'
import { useUserStore } from '../stores/userStore'
import ApiTokenManager from './ApiTokenManager.vue'
import EventRowActions from './EventRowActions.vue'
import WebhookSuccessPopup from './WebhookSuccessPopup.vue'
import axios from '../axios.js'

const props = defineProps({
  show: Boolean
})

const emit = defineEmits(['close'])

const userStore = useUserStore()
const activeTab = ref('tokens') // tokens, documentation, settings, webhooks

// Get current user for token management
const currentUser = computed(() => userStore.currentUser)

// Available webhook events
const availableEvents = ref([
  { key: 'fixture.created', name: 'Fixture Created', description: 'Triggered when a new fixture is created' },
  { key: 'fixture.updated', name: 'Fixture Updated', description: 'Triggered when fixture data is updated' },
  { key: 'fixture.closed', name: 'Fixture Closed', description: 'Triggered when a fixture is closed' },
  { key: 'fixture.settlement', name: 'Fixture Settlement', description: 'Triggered when a fixture is settled' },
  // { key: 'fixture.deleted', name: 'Fixture Deleted', description: 'Triggered when a fixture is deleted' },
  // { key: 'market.created', name: 'Market Created', description: 'Triggered when a new market is created' },
  // { key: 'market.updated', name: 'Market Updated', description: 'Triggered when market odds are updated' },
  // { key: 'market.deleted', name: 'Market Deleted', description: 'Triggered when a market is deleted' },
  // { key: 'player.created', name: 'Player Created', description: 'Triggered when a new player is added' },
  // { key: 'player.updated', name: 'Player Updated', description: 'Triggered when player data is updated' },
  // { key: 'team.created', name: 'Team Created', description: 'Triggered when a new team is added' },
  // { key: 'team.updated', name: 'Team Updated', description: 'Triggered when team data is updated' }
])

// Webhook management state - event-centric approach
const eventEndpoints = ref(
  availableEvents.value.map(event => ({
    id: event.key,
    eventKey: event.key,
    eventName: event.name,
    eventDescription: event.description,
    endpointUrl: '/api/webhook-test', // Default to local webhook receiver
    isActive: true, // Default to active for easier testing
    lastTested: null,
    lastTestStatus: null,
    createdAt: null
  }))
)
const testingEndpoint = ref(null)
const testEventType = ref('fixture.created')
const testEventData = ref('')
const isTestingEvent = ref(false)

// Webhook versions
const webhookVersions = ref([
  { key: 'v1', name: 'Version 1.0', description: 'Original webhook format' },
  { key: 'v2', name: 'Version 2.0', description: 'Enhanced webhook format with metadata' },
  { key: 'v3', name: 'Version 3.0', description: 'Latest webhook format with full context' }
])

const endpointTypes = ref([
  { 
    key: 'webhook', 
    name: 'Webhook Notification', 
    description: 'Send lightweight notifications, client fetches data',
    icon: 'fas fa-bell',
    color: 'blue'
  },
  { 
    key: 'direct_post', 
    name: 'Direct POST', 
    description: 'Send complete data directly to client endpoint',
    icon: 'fas fa-paper-plane',
    color: 'green'
  }
])

// RESTful API testing state
const restEndpoints = ref([])
const showAddRestEndpointForm = ref(false)
const newRestEndpoint = ref({
  name: '',
  url: '',
  method: 'GET',
  headers: {},
  body: '',
  version: 'v1',
  description: '',
  isActive: true
})
const availableRestMethods = ref(['GET', 'POST', 'PUT', 'PATCH', 'DELETE'])
const apiVersions = ref([
  { key: 'v1', name: 'API Version 1.0', description: 'Original API format' },
  { key: 'v2', name: 'API Version 2.0', description: 'Enhanced API with pagination' },
  { key: 'v3', name: 'API Version 3.0', description: 'Latest API with full features' }
])
const testingRestEndpoint = ref(null)
const isTestingRestEndpoint = ref(false)

// Demo webhook URL for the example section
const demoWebhookUrl = ref('https://yourserver.com/webhooks/oddscomposer')

// Edit event endpoint state
const editingEvent = ref(null)
const showEditForm = ref(false)
const editEventEndpoint = ref({
  endpointUrl: ''
})

// Log viewer state
const showLogViewer = ref(false)
const viewingEventLogs = ref(null)
const webhookLogs = ref([])
const expandedLogRows = ref(new Set()) // Track which rows have expanded logs
const expandedIndividualLogs = ref(new Set()) // Track which individual logs are expanded
const showLogDetailPopup = ref(false)
const selectedLogDetail = ref(null)

// Response builder state
const showResponseBuilder = ref(false)
const buildingEventType = ref('')
const responseBuilderConfig = ref({
  fixtureId: 143, // Default fixture ID for testing
  webhookVersion: 'v3',
  // Root fields selection
  includeEvent: true,
  includeFixtureId: true,
  includeTimestamp: true,
  includeStartTime: true,
  includeLeagueId: true,
  includeHomeTeamId: true,
  includeAwayTeamId: true,
  includeHomeTeamName: false,
  includeAwayTeamName: false,
  includeLeagueName: false,
  // Array fields selection
  includePlayerLines: true,
  includeSpecials: true,
  includeBalancedLines: false,
  includeMilestones: false,
  // Player lines field selection
  playerLinesFields: {
    id: true,
    fixture_id: true,
    market_type: true,
    balance_line: true,
    milestone_line: true,
    balance_line_over_odds: true,
    balance_line_under_odds: true,
    milestone_over_odds: true,
    is_suspended: true,
    is_closed: true,
    player_id: true,
    is_balanced: true
  },
  // Specials field selection
  specialsFields: {
    id: true,
    fixture_id: true,
    market_type: true,
    selection_name: true,
    odds: true,
    is_suspended: true,
    status: true,
    settlement: true
  },
  customFields: []
})

// Available fixture.created fields based on backend structure
const fixtureCreatedFields = ref([
  // Root fields
  { key: 'event', name: 'Event Type', type: 'string', required: true, description: 'Event identifier', value: 'fixture.created' },
  { key: 'fixture_id', name: 'Fixture ID', type: 'integer', required: true, description: 'Unique fixture identifier' },
  { key: 'timestamp', name: 'Timestamp', type: 'datetime', required: true, description: 'Event occurrence time' },
  { key: 'start_time', name: 'Game Start Time', type: 'datetime', required: true, description: 'Fixture start time', value: null },
  { key: 'league_id', name: 'League ID', type: 'integer', required: false, description: 'League identifier' },
  { key: 'home_team_id', name: 'Home Team ID', type: 'integer', required: false, description: 'Home team identifier' },
  { key: 'away_team_id', name: 'Away Team ID', type: 'integer', required: false, description: 'Away team identifier' },
  { key: 'home_team_name', name: 'Home Team Name', type: 'string', required: false, description: 'Home team name' },
  { key: 'away_team_name', name: 'Away Team Name', type: 'string', required: false, description: 'Away team name' },
  { key: 'league_name', name: 'League Name', type: 'string', required: false, description: 'League name' },
  // Array fields
  { key: 'player_lines', name: 'Player Lines', type: 'array', required: false, description: 'Array of player betting lines', enabled: true },
  { key: 'specials', name: 'Special Markets', type: 'array', required: false, description: 'Special betting markets', enabled: true },
  { key: 'balanced_lines', name: 'Balanced Lines', type: 'array', required: false, description: 'Balanced betting lines', enabled: false },
  { key: 'milestones', name: 'Milestones', type: 'array', required: false, description: 'Milestone betting options', enabled: false }
])

// Computed property for response preview
const responsePreview = computed(() => {
  return generateResponsePreview()
})

// Confirmation modal state
const showConfirmModal = ref(false)
const confirmModalData = ref({
  title: '',
  message: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  confirmAction: null,
  type: 'danger' // 'danger', 'warning', 'info'
})

// Success/Error notification state
const showNotification = ref(false)
const notificationData = ref({
  type: 'success', // 'success', 'error', 'info'
  title: '',
  message: ''
})

// Webhook success popup state
const showWebhookSuccessPopup = ref(false)
const webhookSuccessData = ref({})

// Predefined API endpoints for quick testing
const predefinedEndpoints = ref([
  {
    name: 'Get All Teams',
    url: '/api/teams',
    method: 'GET',
    description: 'Retrieve list of all teams',
    version: 'v1',
    category: 'Teams'
  },
  {
    name: 'Get Team by ID',
    url: '/api/teams/{id}',
    method: 'GET',
    description: 'Get specific team details',
    version: 'v1',
    category: 'Teams'
  },
  {
    name: 'Get All Players',
    url: '/api/players',
    method: 'GET',
    description: 'Retrieve list of all players',
    version: 'v1',
    category: 'Players'
  },
  {
    name: 'Get Player by ID',
    url: '/api/players/{id}',
    method: 'GET',
    description: 'Get specific player details',
    version: 'v1',
    category: 'Players'
  },
  {
    name: 'Get All Fixtures',
    url: '/api/fixtures',
    method: 'GET',
    description: 'Retrieve list of all fixtures',
    version: 'v1',
    category: 'Fixtures'
  },
  {
    name: 'Get Fixture by ID',
    url: '/api/fixtures/{id}',
    method: 'GET',
    description: 'Get specific fixture details',
    version: 'v1',
    category: 'Fixtures'
  },
  {
    name: 'Get Markets for Fixture',
    url: '/api/fixtures/{id}/markets',
    method: 'GET',
    description: 'Get all markets for a specific fixture',
    version: 'v1',
    category: 'Markets'
  },
  {
    name: 'Get All Leagues',
    url: '/api/leagues',
    method: 'GET',
    description: 'Retrieve list of all leagues',
    version: 'v1',
    category: 'Leagues'
  },

  {
    name: 'Get All Markets',
    url: '/api/markets',
    method: 'GET',
    description: 'Retrieve list of all markets',
    version: 'v1',
    category: 'Markets'
  }
  
])

const handleTokenGenerated = (token) => {
  console.log('Token generated:', token)
}

const handleTokenRevoked = (tokenId) => {
  console.log('Token revoked:', tokenId)
}

// Webhook management functions
const addWebhookEndpoint = () => {
  if (!newEndpoint.value.name || !newEndpoint.value.url) {
    alert('Please fill in name and URL')
    return
  }
  
  const endpoint = {
    id: Date.now().toString(),
    ...newEndpoint.value,
    createdAt: new Date().toISOString(),
    lastTested: null,
    status: 'active'
  }
  
  webhookEndpoints.value.push(endpoint)
  
  // Reset form
  newEndpoint.value = {
    name: '',
    url: '',
    description: '',
    events: [],
    version: 'v1',
    type: 'webhook',
    isActive: true
  }
  showAddEndpointForm.value = false
}

const deleteWebhookEndpoint = (endpointId) => {
  if (confirm('Are you sure you want to delete this webhook endpoint?')) {
    webhookEndpoints.value = webhookEndpoints.value.filter(e => e.id !== endpointId)
  }
}

const toggleEndpointStatus = (endpointId) => {
  const endpoint = webhookEndpoints.value.find(e => e.id === endpointId)
  if (endpoint) {
    endpoint.isActive = !endpoint.isActive
  }
}

const testWebhookEndpoint = async (endpoint) => {
  if (!endpoint.isActive) {
    alert('Endpoint is disabled. Please enable it first.')
    return
  }
  
  isTestingEvent.value = true
  testingEndpoint.value = endpoint
  
  try {
    let sampleData, headers, body
    
    if (endpoint.type === 'webhook') {
      // Webhook notification - lightweight payload
      sampleData = {
        event: testEventType.value,
        timestamp: new Date().toISOString(),
        dataId: Math.floor(Math.random() * 10000), // Just an ID reference
        apiEndpoint: `/api/v1/${testEventType.value.replace('.', '/')}`, // Where to fetch full data
        signature: 'test-signature-' + Date.now()
      }
      
      headers = {
        'Content-Type': 'application/json',
        'User-Agent': 'OddsComposer-Webhook-Test/1.0',
        'X-Webhook-Event': testEventType.value,
        'X-Webhook-Version': endpoint.version || 'v1',
        'X-Webhook-Type': 'notification'
      }
    } else {
      // Direct POST - complete data payload
      sampleData = generateSampleEventData(testEventType.value)
      
      headers = {
        'Content-Type': 'application/json',
        'User-Agent': 'OddsComposer-DirectPost-Test/1.0',
        'X-Event-Type': testEventType.value,
        'X-API-Version': endpoint.version || 'v1',
        'X-Post-Type': 'direct_data'
      }
    }
    
    const response = await fetch(endpoint.url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(sampleData)
    })
    
    // Update endpoint with test result
    endpoint.lastTested = new Date().toISOString()
    endpoint.lastTestStatus = response.ok ? 'success' : 'failed'
    endpoint.lastTestResponse = {
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries())
    }
    
    if (response.ok) {
      alert(`✅ Test successful! Status: ${response.status}`)
    } else {
      alert(`❌ Test failed! Status: ${response.status} - ${response.statusText}`)
    }
    
  } catch (error) {
    endpoint.lastTested = new Date().toISOString()
    endpoint.lastTestStatus = 'error'
    endpoint.lastTestResponse = {
      error: error.message
    }
    alert(`❌ Test failed! Error: ${error.message}`)
  } finally {
    isTestingEvent.value = false
    testingEndpoint.value = null
  }
}

const generateSampleEventData = (eventType) => {
  const baseData = {
    event: eventType,
    timestamp: new Date().toISOString(),
    data: {}
  }
  
  switch (eventType) {
    case 'fixture.created':
    case 'fixture.updated':
      baseData.data = {
        id: 12345,
        homeTeam: 'Lakers',
        awayTeam: 'Warriors',
        startTime: new Date(Date.now() + 86400000).toISOString(),
        league: 'NBA',
        status: 'scheduled'
      }
      break
    case 'market.created':
    case 'market.updated':
      baseData.data = {
        id: 67890,
        fixtureId: 12345,
        name: 'Moneyline',
        odds: {
          home: 1.85,
          away: 1.95
        },
        status: 'active'
      }
      break
    case 'player.created':
    case 'player.updated':
      baseData.data = {
        id: 11111,
        name: 'LeBron James',
        team: 'Lakers',
        position: 'SF',
        jerseyNumber: 6
      }
      break
    case 'team.created':
    case 'team.updated':
      baseData.data = {
        id: 22222,
        name: 'Los Angeles Lakers',
        league: 'NBA',
        city: 'Los Angeles',
        founded: 1947
      }
      break
    default:
      baseData.data = {
        id: 99999,
        message: 'Sample test data'
      }
  }
  
  return baseData
}

// RESTful API management functions
const addRestEndpoint = () => {
  if (!newRestEndpoint.value.name || !newRestEndpoint.value.url) {
    alert('Please fill in name and URL')
    return
  }
  
  const endpoint = {
    id: Date.now().toString(),
    ...newRestEndpoint.value,
    createdAt: new Date().toISOString(),
    lastTested: null,
    status: 'active'
  }
  
  restEndpoints.value.push(endpoint)
  
  // Reset form
  newRestEndpoint.value = {
    name: '',
    url: '',
    method: 'GET',
    headers: {},
    body: '',
    version: 'v1',
    description: '',
    isActive: true
  }
  showAddRestEndpointForm.value = false
}

const addPredefinedEndpoint = (predefined) => {
  const endpoint = {
    id: Date.now().toString(),
    name: predefined.name,
    url: predefined.url,
    method: predefined.method,
    headers: {},
    body: '',
    version: predefined.version,
    description: predefined.description,
    isActive: true,
    createdAt: new Date().toISOString(),
    lastTested: null,
    status: 'active'
  }
  
  restEndpoints.value.push(endpoint)
}

// Event endpoint management functions
const startEditEventEndpoint = (eventEndpoint) => {
  editingEvent.value = eventEndpoint
  editEventEndpoint.value = {
    endpointUrl: eventEndpoint.endpointUrl
  }
  showEditForm.value = true
}

const saveEventEndpoint = () => {
  if (!editEventEndpoint.value.endpointUrl) {
    showNotification.value = true
    notificationData.value = {
      type: 'error',
      title: 'Missing URL',
      message: 'Please enter an endpoint URL'
    }
    return
  }
  
  editingEvent.value.endpointUrl = editEventEndpoint.value.endpointUrl
  editingEvent.value.isActive = true
  editingEvent.value.createdAt = editingEvent.value.createdAt || new Date().toISOString()
  
  showEditForm.value = false
  editingEvent.value = null
  
  showNotification.value = true
  notificationData.value = {
    type: 'success',
    title: 'Success!',
    message: 'Event endpoint updated successfully'
  }
}

const cancelEdit = () => {
  showEditForm.value = false
  editingEvent.value = null
  editEventEndpoint.value = {
    endpointUrl: ''
  }
}

const toggleEventStatus = (eventKey) => {
  const eventEndpoint = eventEndpoints.value.find(e => e.eventKey === eventKey)
  if (eventEndpoint) {
    eventEndpoint.isActive = !eventEndpoint.isActive
  }
}

const removeEventEndpoint = (eventKey) => {
  const eventEndpoint = eventEndpoints.value.find(e => e.eventKey === eventKey)
  if (!eventEndpoint) return
  
  showConfirmModal.value = true
  confirmModalData.value = {
    title: 'Remove Endpoint',
    message: `Are you sure you want to remove the endpoint for "${eventEndpoint.eventName}"? This will stop webhook notifications for this event.`,
    confirmText: 'Remove',
    cancelText: 'Cancel',
    type: 'danger',
    confirmAction: () => {
      eventEndpoint.endpointUrl = ''
      eventEndpoint.isActive = false
      eventEndpoint.lastTested = null
      eventEndpoint.lastTestStatus = null
      eventEndpoint.createdAt = null
      
      showNotification.value = true
      notificationData.value = {
        type: 'success',
        title: 'Removed',
        message: 'Endpoint removed successfully'
      }
    }
  }
}

// Log viewer functions
const showEventLogs = (eventEndpoint) => {
  viewingEventLogs.value = eventEndpoint
  // Show real logs for this event (will be populated by addTestLog)
  webhookLogs.value = []
  showLogViewer.value = true
}

const closeLogViewer = () => {
  showLogViewer.value = false
  viewingEventLogs.value = null
  webhookLogs.value = []
}

// Modal helper functions
const confirmAction = () => {
  if (confirmModalData.value.confirmAction) {
    confirmModalData.value.confirmAction()
  }
  showConfirmModal.value = false
}

const cancelConfirm = () => {
  showConfirmModal.value = false
}

const closeNotification = () => {
  showNotification.value = false
}

const closeWebhookSuccessPopup = () => {
  showWebhookSuccessPopup.value = false
  webhookSuccessData.value = {}
}

// Auto-close notification after 5 seconds
const showNotificationWithTimeout = (data) => {
  notificationData.value = data
  showNotification.value = true
  setTimeout(() => {
    showNotification.value = false
  }, 5000)
}

// Toggle inline logs for event row
const toggleInlineLogs = (eventKey) => {
  if (expandedLogRows.value.has(eventKey)) {
    expandedLogRows.value.delete(eventKey)
  } else {
    expandedLogRows.value.add(eventKey)
  }
}

// Get real logs for an event (inline version)
const getInlineLogsForEvent = (eventKey) => {
  // Return real logs from webhookLogs that match this event
  return webhookLogs.value.filter(log => log.event === eventKey)
}

// Toggle individual log expansion
const toggleIndividualLog = (logId) => {
  if (expandedIndividualLogs.value.has(logId)) {
    expandedIndividualLogs.value.delete(logId)
  } else {
    expandedIndividualLogs.value.add(logId)
  }
}


// Show log detail popup
const showLogDetail = (log) => {
  selectedLogDetail.value = log
  showLogDetailPopup.value = true
}

// Close log detail popup
const closeLogDetail = () => {
  showLogDetailPopup.value = false
  selectedLogDetail.value = null
}

// Response builder functions
const openResponseBuilder = (eventEndpoint) => {
  buildingEventType.value = eventEndpoint.eventKey
  responseBuilderConfig.value = {
    fixtureId: 143, // Default fixture ID for testing
    webhookVersion: 'v3',
    // Root fields selection
    includeEvent: true,
    includeFixtureId: true,
    includeTimestamp: true,
    includeStartTime: true,
    includeLeagueId: true,
    includeHomeTeamId: true,
    includeAwayTeamId: true,
    includeHomeTeamName: false,
    includeAwayTeamName: false,
    includeLeagueName: false,
    // Array fields selection
    includePlayerLines: true,
    includeSpecials: true,
    includeBalancedLines: false,
    includeMilestones: false,
    // Player lines field selection
    playerLinesFields: {
      id: true,
      fixture_id: true,
      market_type: true,
      balance_line: true,
      milestone_line: true,
      balance_line_over_odds: true,
      balance_line_under_odds: true,
      milestone_over_odds: true,
      is_suspended: true,
      is_closed: true,
      player_id: true,
      is_balanced: true
    },
    // Specials field selection
    specialsFields: {
      id: true,
      fixture_id: true,
      market_type: true,
      selection_name: true,
      odds: true,
      is_suspended: true,
      status: true,
      settlement: true
    },
    customFields: []
  }
  showResponseBuilder.value = true
}

const closeResponseBuilder = () => {
  showResponseBuilder.value = false
  buildingEventType.value = ''
}

const updateFieldEnabled = (fieldKey, enabled) => {
  const field = fixtureCreatedFields.value.find(f => f.key === fieldKey)
  if (field) {
    field.enabled = enabled
  }
}

const getFieldConfig = (fieldKey) => {
  switch (fieldKey) {
    case 'event':
      return responseBuilderConfig.value.includeEvent
    case 'fixture_id':
      return responseBuilderConfig.value.includeFixtureId
    case 'timestamp':
      return responseBuilderConfig.value.includeTimestamp
    case 'start_time':
      return responseBuilderConfig.value.includeStartTime
    case 'league_id':
      return responseBuilderConfig.value.includeLeagueId
    case 'home_team_id':
      return responseBuilderConfig.value.includeHomeTeamId
    case 'away_team_id':
      return responseBuilderConfig.value.includeAwayTeamId
    case 'home_team_name':
      return responseBuilderConfig.value.includeHomeTeamName
    case 'away_team_name':
      return responseBuilderConfig.value.includeAwayTeamName
    case 'league_name':
      return responseBuilderConfig.value.includeLeagueName
    case 'player_lines':
      return responseBuilderConfig.value.includePlayerLines
    case 'specials':
      return responseBuilderConfig.value.includeSpecials
    case 'balanced_lines':
      return responseBuilderConfig.value.includeBalancedLines
    case 'milestones':
      return responseBuilderConfig.value.includeMilestones
    default:
      return false
  }
}

const setFieldConfig = (fieldKey, value) => {
  switch (fieldKey) {
    case 'event':
      responseBuilderConfig.value.includeEvent = value
      break
    case 'fixture_id':
      responseBuilderConfig.value.includeFixtureId = value
      break
    case 'timestamp':
      responseBuilderConfig.value.includeTimestamp = value
      break
    case 'start_time':
      responseBuilderConfig.value.includeStartTime = value
      break
    case 'league_id':
      responseBuilderConfig.value.includeLeagueId = value
      break
    case 'home_team_id':
      responseBuilderConfig.value.includeHomeTeamId = value
      break
    case 'away_team_id':
      responseBuilderConfig.value.includeAwayTeamId = value
      break
    case 'home_team_name':
      responseBuilderConfig.value.includeHomeTeamName = value
      break
    case 'away_team_name':
      responseBuilderConfig.value.includeAwayTeamName = value
      break
    case 'league_name':
      responseBuilderConfig.value.includeLeagueName = value
      break
    case 'player_lines':
      responseBuilderConfig.value.includePlayerLines = value
      break
    case 'specials':
      responseBuilderConfig.value.includeSpecials = value
      break
    case 'balanced_lines':
      responseBuilderConfig.value.includeBalancedLines = value
      break
    case 'milestones':
      responseBuilderConfig.value.includeMilestones = value
      break
  }
}

// Helper functions for nested field configurations
const getNestedFieldConfig = (section, fieldKey) => {
  if (section === 'playerLinesFields') {
    return responseBuilderConfig.value.playerLinesFields[fieldKey] || false
  } else if (section === 'specialsFields') {
    return responseBuilderConfig.value.specialsFields[fieldKey] || false
  }
  return false
}

const setNestedFieldConfig = (section, fieldKey, value) => {
  if (section === 'playerLinesFields') {
    responseBuilderConfig.value.playerLinesFields[fieldKey] = value
  } else if (section === 'specialsFields') {
    responseBuilderConfig.value.specialsFields[fieldKey] = value
  }
}

const getFieldPreview = (fieldKey) => {
  const config = responseBuilderConfig.value
  const now = new Date()
  
  const fieldPreviews = {
    event: '"fixture.created"',
    fixture_id: config.fixtureId.toString(),
    timestamp: `"${now.toISOString()}"`,
    start_time: `"${now.toISOString()}"`,
    league_id: '123',
    home_team_id: '456',
    away_team_id: '789',
    home_team_name: '"Lakers"',
    away_team_name: '"Warriors"',
    league_name: '"NBA"',
    player_lines: '[{ id: 1, fixture_id: ' + config.fixtureId + ', market_type: "points", balance_line: 25.5, ... }]',
    specials: '[{ id: 1, fixture_id: ' + config.fixtureId + ', market_type: "player_props", selection_name: "Player Points", ... }]',
    balanced_lines: '[]',
    milestones: '[]'
  }
  
  return fieldPreviews[fieldKey] || 'null'
}

const getFormattedFieldPreview = (fieldKey) => {
  const preview = getFieldPreview(fieldKey)
  
  // Color different types of values with pastel colors
  if (preview.startsWith('"') && preview.endsWith('"')) {
    // String values in pastel green
    return `<span class="text-green-300">${preview}</span>`
  } else if (preview.startsWith('[') && preview.endsWith(']')) {
    // Array values in pastel purple
    return `<span class="text-purple-300">${preview}</span>`
  } else if (!isNaN(preview) || preview === 'null' || preview === 'true' || preview === 'false') {
    // Numbers and booleans in pastel orange
    return `<span class="text-orange-300">${preview}</span>`
  } else {
    // Default pastel gray for others
    return `<span class="text-gray-500">${preview}</span>`
  }
}

const formatJsonWithColors = (json) => {
  const jsonString = JSON.stringify(json, null, 2)
  
  return jsonString
    .replace(/("[\w\s-_:.]+")\s*:/g, '<span class="text-blue-300">$1</span>:') // Keys in pastel blue
    .replace(/:\s*("[\w\s/.:\-_]+")/g, ': <span class="text-green-300">$1</span>') // String values in pastel green
    .replace(/:\s*(\d+\.?\d*)/g, ': <span class="text-orange-300">$1</span>') // Numbers in pastel orange
    .replace(/:\s*(true|false|null)/g, ': <span class="text-yellow-300">$1</span>') // Booleans and null in pastel yellow
    .replace(/:\s*(\[[^\]]*\])/g, ': <span class="text-purple-300">$1</span>') // Arrays in pastel purple
    .replace(/:\s*(\{[^}]*\})/g, ': <span class="text-cyan-300">$1</span>') // Objects in pastel cyan
}

const generateResponsePreview = () => {
  if (buildingEventType.value !== 'fixture.created') {
    return { error: 'Response builder only supports fixture.created for now' }
  }

  const config = responseBuilderConfig.value
  const now = new Date()
  
  // Build response data conditionally based on config
  const responseData = {}

  // Add root fields based on configuration
  if (config.includeEvent) {
    responseData.event = 'fixture.created'
  }
  
  if (config.includeFixtureId) {
    responseData.fixture_id = config.fixtureId
  }
  
  if (config.includeTimestamp) {
    responseData.timestamp = now.toISOString()
  }
  
  if (config.includeStartTime) {
    responseData.start_time = now.toISOString() // This would be actual fixture start time
  }
  
  if (config.includeLeagueId) {
    responseData.league_id = 123 // Would be populated from actual fixture data
  }
  
  if (config.includeHomeTeamId) {
    responseData.home_team_id = 456 // Would be populated from actual fixture data
  }
  
  if (config.includeAwayTeamId) {
    responseData.away_team_id = 789 // Would be populated from actual fixture data
  }
  
  if (config.includeHomeTeamName) {
    responseData.home_team_name = 'Lakers' // Would be populated from actual fixture data
  }
  
  if (config.includeAwayTeamName) {
    responseData.away_team_name = 'Warriors' // Would be populated from actual fixture data
  }
  
  if (config.includeLeagueName) {
    responseData.league_name = 'NBA' // Would be populated from actual fixture data
  }

  // Add conditional array fields based on config
  if (config.includePlayerLines) {
    const playerLine = {}
    if (config.playerLinesFields.id) playerLine.id = 1
    if (config.playerLinesFields.fixture_id) playerLine.fixture_id = config.fixtureId
    if (config.playerLinesFields.market_type) playerLine.market_type = 'points'
    if (config.playerLinesFields.balance_line) playerLine.balance_line = 25.5
    if (config.playerLinesFields.milestone_line) playerLine.milestone_line = 30
    if (config.playerLinesFields.balance_line_over_odds) playerLine.balance_line_over_odds = 1.85
    if (config.playerLinesFields.balance_line_under_odds) playerLine.balance_line_under_odds = 1.95
    if (config.playerLinesFields.milestone_over_odds) playerLine.milestone_over_odds = 2.15
    if (config.playerLinesFields.is_suspended) playerLine.is_suspended = false
    if (config.playerLinesFields.is_closed) playerLine.is_closed = false
    if (config.playerLinesFields.player_id) playerLine.player_id = 123
    if (config.playerLinesFields.is_balanced) playerLine.is_balanced = true
    
    responseData.player_lines = [playerLine]
  }

  if (config.includeSpecials) {
    const special = {}
    if (config.specialsFields.id) special.id = 1
    if (config.specialsFields.fixture_id) special.fixture_id = config.fixtureId
    if (config.specialsFields.market_type) special.market_type = 'player_props'
    if (config.specialsFields.selection_name) special.selection_name = 'Player Points'
    if (config.specialsFields.odds) special.odds = 1.90
    if (config.specialsFields.is_suspended) special.is_suspended = false
    if (config.specialsFields.status) special.status = 'active'
    if (config.specialsFields.settlement) special.settlement = null
    
    responseData.specials = [special]
  }

  if (config.includeBalancedLines) {
    responseData.balanced_lines = []
  }

  if (config.includeMilestones) {
    responseData.milestones = []
  }

  return responseData
}

const testResponseBuilder = async (eventEndpoint) => {
  const responseData = generateResponsePreview()
  
  if (responseData.error) {
    showNotificationWithTimeout({
      type: 'error',
      title: 'Builder Error',
      message: responseData.error
    })
    return
  }

  try {
    // Send test request to backend using the response builder config
    const response = await axios.post('/api/webhooks/test/v2', {
      eventKey: eventEndpoint.eventKey,
      endpointUrl: eventEndpoint.endpointUrl,
      webhookVersion: responseBuilderConfig.value.webhookVersion,
      responseBuilderConfig: responseBuilderConfig.value,
      testPayload: responseData
    })
    
    const result = response.data
    
    if (response.status === 200 && result.success) {
      showNotificationWithTimeout({
        type: 'success',
        title: 'Response Builder Test Successful!',
        message: `Test sent with custom configuration. Response: ${result.response?.status || 'OK'}`
      })
      
      // Add test log
      addTestLog(eventEndpoint.eventKey, {
        status: 'success',
        responseTime: result.responseTime || 0,
        statusCode: result.response?.status || 200,
        payload: responseData,
        response: result.response
      })
      
      // Close builder
      closeResponseBuilder()
    } else {
      showNotificationWithTimeout({
        type: 'error',
        title: 'Response Builder Test Failed',
        message: result.message || 'Test failed'
      })
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Unknown error'
    showNotificationWithTimeout({
      type: 'error',
      title: 'Response Builder Error',
      message: `Network error: ${errorMessage}`
    })
  }
}

const deleteRestEndpoint = (endpointId) => {
  if (confirm('Are you sure you want to delete this REST endpoint?')) {
    restEndpoints.value = restEndpoints.value.filter(e => e.id !== endpointId)
  }
}

const toggleRestEndpointStatus = (endpointId) => {
  const endpoint = restEndpoints.value.find(e => e.id === endpointId)
  if (endpoint) {
    endpoint.isActive = !endpoint.isActive
  }
}

/**
 * Test Event Endpoint
 * 
 * Sends a test webhook request to the backend API endpoint.
 * 
 * Expected Backend API Endpoint: POST /api/webhooks/test
 * 
 * Request Body:
 * {
 *   "eventKey": "fixture.created",
 *   "endpointUrl": "https://client.example.com/webhook",
 *   "testPayload": { ... },
 *   "webhookVersion": "v3"
 * }
 * 
 * Expected Response:
 * {
 *   "success": true,
 *   "message": "Webhook test sent successfully",
 *   "responseTime": 150,
 *   "response": {
 *     "status": 200,
 *     "statusText": "OK",
 *     "headers": { ... },
 *     "body": "OK"
 *   }
 * }
 */
const switchToLocalReceiver = (eventEndpoint) => {
  eventEndpoint.endpointUrl = '/api/webhook-test'
  eventEndpoint.isActive = true
  
  showNotificationWithTimeout({
    type: 'success',
    title: 'Switched to Local Receiver',
    message: 'Now using local webhook receiver for testing'
  })
}

const testEventEndpoint = async (eventEndpoint) => {
  if (!eventEndpoint.isActive || !eventEndpoint.endpointUrl) {
    showNotificationWithTimeout({
      type: 'error',
      title: 'Cannot Test',
      message: 'Endpoint must be active and have a URL configured'
    })
    return
  }
  
  isTestingEvent.value = true
  testingEndpoint.value = eventEndpoint
  
  try {
    // Send test request to backend using axios
    // Backend will generate realistic test payload using real data from fixture_id 143
    const response = await axios.post('/api/webhooks/test/v2', {
      eventKey: eventEndpoint.eventKey,
      endpointUrl: eventEndpoint.endpointUrl,
      webhookVersion: 'v3'
      // testPayload is not needed - backend generates real data
    })
    
    const result = response.data
    
    // Check if the API call was successful AND the webhook response was successful
    if (response.status === 200 && result.response_status === 200) {
      // Update endpoint with test result
      eventEndpoint.lastTested = new Date().toISOString()
      eventEndpoint.lastTestStatus = 'success'
      
      // Show success popup for 200 responses
      webhookSuccessData.value = {
        status: result.response_status || 200,
        responseTime: result.responseTime || 0,
        endpointUrl: eventEndpoint.endpointUrl,
        eventKey: eventEndpoint.eventKey,
        webhook_url: result.webhook_url || result.response?.webhook_url,
        response: result.response
      }
      showWebhookSuccessPopup.value = true
      
      // Add test log to webhook logs
      addTestLog(eventEndpoint.eventKey, {
        status: 'success',
        responseTime: result.responseTime || 0,
        statusCode: result.response_status || 200,
        payload: result.request || {},
        response: {
          status: result.response_status,
          body: result.response_body,
          headers: {}
        }
      })
      
    } else {
      eventEndpoint.lastTested = new Date().toISOString()
      eventEndpoint.lastTestStatus = 'failed'
      
      showNotificationWithTimeout({
        type: 'error',
        title: 'Test Failed',
        message: result.message || `Webhook test failed with status ${result.response_status || 'unknown'}`
      })
      
      // Add test log to webhook logs
      addTestLog(eventEndpoint.eventKey, {
        status: 'failed',
        responseTime: result.responseTime || 0,
        statusCode: result.response_status || 500,
        payload: result.request || {},
        response: {
          status: result.response_status,
          body: result.response_body,
          headers: {}
        },
        error: result.message
      })
    }
    
  } catch (error) {
    eventEndpoint.lastTested = new Date().toISOString()
    eventEndpoint.lastTestStatus = 'error'
    
    // Handle axios error response
    const errorMessage = error.response?.data?.message || error.message || 'Unknown error'
    const statusCode = error.response?.status || 0
    
    showNotificationWithTimeout({
      type: 'error',
      title: 'Test Error',
      message: `Network error: ${errorMessage}`
    })
    
    // Add test log to webhook logs
    addTestLog(eventEndpoint.eventKey, {
      status: 'error',
      responseTime: 0,
      statusCode: statusCode,
      payload: { error: 'Failed to generate test payload' },
      response: error.response?.data || null,
      error: errorMessage
    })
    
  } finally {
    isTestingEvent.value = false
    testingEndpoint.value = null
  }
}

// Test payload generation is now handled by the backend using real data from fixture_id 143

// Local receiver testing is now handled by the main testEventEndpoint method

const addTestLog = (eventKey, logData) => {
  const newLog = {
    id: `test_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    timestamp: new Date().toISOString(),
    event: eventKey,
    ...logData
  }
  
  // Add to webhook logs if log viewer is open for this event
  if (viewingEventLogs.value?.eventKey === eventKey) {
    webhookLogs.value.unshift(newLog)
  }
}

const testRestEndpoint = async (endpoint) => {
  if (!endpoint.isActive) {
    alert('Endpoint is disabled. Please enable it first.')
    return
  }
  
  isTestingRestEndpoint.value = true
  testingRestEndpoint.value = endpoint
  
  try {
    const headers = {
      'Content-Type': 'application/json',
      'User-Agent': 'OddsComposer-API-Test/1.0',
      'X-API-Version': endpoint.version || 'v1',
      ...endpoint.headers
    }
    
    const requestOptions = {
      method: endpoint.method,
      headers: headers
    }
    
    // Add body for methods that support it
    if (['POST', 'PUT', 'PATCH'].includes(endpoint.method) && endpoint.body) {
      requestOptions.body = endpoint.body
    }
    
    const response = await fetch(endpoint.url, requestOptions)
    
    // Update endpoint with test result
    endpoint.lastTested = new Date().toISOString()
    endpoint.lastTestStatus = response.ok ? 'success' : 'failed'
    endpoint.lastTestResponse = {
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries())
    }
    
    // Try to get response body
    try {
      const responseText = await response.text()
      endpoint.lastTestResponse.body = responseText
    } catch (e) {
      endpoint.lastTestResponse.body = 'Could not read response body'
    }
    
    if (response.ok) {
      alert(`✅ Test successful! Status: ${response.status}`)
    } else {
      alert(`❌ Test failed! Status: ${response.status} - ${response.statusText}`)
    }
    
  } catch (error) {
    endpoint.lastTested = new Date().toISOString()
    endpoint.lastTestStatus = 'error'
    endpoint.lastTestResponse = {
      error: error.message
    }
    alert(`❌ Test failed! Error: ${error.message}`)
  } finally {
    isTestingRestEndpoint.value = false
    testingRestEndpoint.value = null
  }
}

const closePopup = () => {
  emit('close')
}

// Handle escape key
const handleKeyDown = (event) => {
  if (event.key === 'Escape' && props.show) {
    closePopup()
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeyDown)
})

// Clean up event listener
import { onBeforeUnmount } from 'vue'
onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeyDown)
})
</script>

<template>
  <div v-if="show" 
       class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center overflow-y-auto"
       @click.self="closePopup"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full h-screen max-w-[95vw] max-h-[95vh] m-4 overflow-hidden flex">
      <!-- Left Sidebar -->
      <div class="w-64 bg-gray-50 border-r border-gray-200 flex flex-col">
        <!-- Header -->
        <div class="p-6 border-b border-gray-200">
          <div>
            <h3 class="text-lg font-semibold text-gray-900">API Manager</h3>
            <p class="text-sm text-gray-600 mt-1">Manage your API tokens and settings</p>
          </div>
        </div>

        <!-- Vertical Menu -->
        <nav class="flex-1 p-4 space-y-2">
          <button
            @click="activeTab = 'tokens'"
            class="w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors"
            :class="activeTab === 'tokens' 
              ? 'bg-pink-100 text-pink-700 border border-pink-200' 
              : 'text-gray-700 hover:bg-gray-100'"
          >
            <i class="fas fa-key mr-3 text-lg"></i>
            <div>
              <div class="font-medium">API Tokens</div>
              <div class="text-xs text-gray-500">Manage authentication</div>
            </div>
          </button>
          
          <!-- <button
            @click="activeTab = 'documentation'"
            class="w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors"
            :class="activeTab === 'documentation' 
              ? 'bg-pink-100 text-pink-700 border border-pink-200' 
              : 'text-gray-700 hover:bg-gray-100'"
          >
            <i class="fas fa-book mr-3 text-lg"></i>
            <div>
              <div class="font-medium">Documentation</div>
              <div class="text-xs text-gray-500">API guides & examples</div>
            </div>
          </button> -->
          
          <button
            @click="activeTab = 'webhooks'"
            class="w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors"
            :class="activeTab === 'webhooks' 
              ? 'bg-pink-100 text-pink-700 border border-pink-200' 
              : 'text-gray-700 hover:bg-gray-100'"
          >
            <i class="fas fa-plug mr-3 text-lg"></i>
            <div>
              <div class="font-medium">Webhooks</div>
              <div class="text-xs text-gray-500">Event notifications</div>
            </div>
          </button>
          
          <button
            @click="activeTab = 'restful'"
            class="w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors"
            :class="activeTab === 'restful' 
              ? 'bg-pink-100 text-pink-700 border border-pink-200' 
              : 'text-gray-700 hover:bg-gray-100'"
          >
            <i class="fas fa-code mr-3 text-lg"></i>
            <div>
              <div class="font-medium">RESTful API</div>
              <div class="text-xs text-gray-500">HTTP endpoint testing</div>
            </div>
          </button>
          
          <!-- <button
            @click="activeTab = 'settings'"
            class="w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors"
            :class="activeTab === 'settings' 
              ? 'bg-pink-100 text-pink-700 border border-pink-200' 
              : 'text-gray-700 hover:bg-gray-100'"
          >
            <i class="fas fa-cog mr-3 text-lg"></i>
            <div>
              <div class="font-medium">Settings</div>
              <div class="text-xs text-gray-500">API configuration</div>
            </div>
          </button> -->
        </nav>
      </div>

      <!-- Main Content Area -->
      <div class="flex-1 flex flex-col">
        <!-- Header with Close Button -->
        <div class="flex items-center justify-end p-4 border-b border-gray-200">
          <button @click="closePopup" 
                  class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <!-- Content -->
        <div class="p-6 flex-1 overflow-y-auto">
        <!-- API Tokens Tab -->
        <div v-if="activeTab === 'tokens'">
          <ApiTokenManager 
            v-if="currentUser"
            :user="currentUser"
            :user-store="userStore"
            @token-generated="handleTokenGenerated"
            @token-revoked="handleTokenRevoked"
          />
          <div v-else class="text-center py-8 bg-gray-50 rounded-lg">
            <div class="text-gray-400 mb-4">
              <i class="fas fa-user-slash text-4xl"></i>
            </div>
            <div class="text-gray-600 mb-2">No user logged in</div>
            <div class="text-sm text-gray-500">
              Please log in to manage API tokens
            </div>
          </div>
        </div>

        <!-- Documentation Tab -->
        <div v-if="activeTab === 'documentation'" class="space-y-6">
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div class="flex items-start">
              <div class="flex-shrink-0">
                <i class="fas fa-info-circle text-blue-500 text-xl"></i>
              </div>
              <div class="ml-3">
                <h3 class="text-lg font-medium text-blue-900">API Documentation</h3>
                <p class="mt-1 text-sm text-blue-700">
                  Access comprehensive API documentation and examples for integrating with OddsComposer.
                </p>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="bg-white border border-gray-200 rounded-lg p-6">
              <div class="flex items-center mb-4">
                <i class="fas fa-code text-gray-600 text-xl mr-3"></i>
                <h4 class="text-lg font-medium text-gray-900">Quick Start</h4>
              </div>
              <p class="text-gray-600 text-sm mb-4">
                Get started with our API in minutes. Learn the basics of authentication and making your first request.
              </p>
              <button class="text-pink-600 hover:text-pink-700 text-sm font-medium">
                View Quick Start Guide →
              </button>
            </div>

            <div class="bg-white border border-gray-200 rounded-lg p-6">
              <div class="flex items-center mb-4">
                <i class="fas fa-list text-gray-600 text-xl mr-3"></i>
                <h4 class="text-lg font-medium text-gray-900">API Reference</h4>
              </div>
              <p class="text-gray-600 text-sm mb-4">
                Complete reference for all available endpoints, parameters, and response formats.
              </p>
              <button class="text-pink-600 hover:text-pink-700 text-sm font-medium">
                Browse API Reference →
              </button>
            </div>

            <div class="bg-white border border-gray-200 rounded-lg p-6">
              <div class="flex items-center mb-4">
                <i class="fas fa-code-branch text-gray-600 text-xl mr-3"></i>
                <h4 class="text-lg font-medium text-gray-900">SDKs & Libraries</h4>
              </div>
              <p class="text-gray-600 text-sm mb-4">
                Official SDKs and community libraries for popular programming languages.
              </p>
              <button class="text-pink-600 hover:text-pink-700 text-sm font-medium">
                View SDKs →
              </button>
            </div>

            <div class="bg-white border border-gray-200 rounded-lg p-6">
              <div class="flex items-center mb-4">
                <i class="fas fa-question-circle text-gray-600 text-xl mr-3"></i>
                <h4 class="text-lg font-medium text-gray-900">Support</h4>
              </div>
              <p class="text-gray-600 text-sm mb-4">
                Need help? Contact our support team or join our developer community.
              </p>
              <button class="text-pink-600 hover:text-pink-700 text-sm font-medium">
                Get Support →
              </button>
            </div>
          </div>
        </div>

        <!-- Webhooks Tab -->
        <div v-if="activeTab === 'webhooks'" class="space-y-6">
          <!-- Header -->
          <div>
            <h4 class="text-lg font-medium text-gray-900">Webhook Events</h4>
            <p class="text-sm text-gray-600 mt-1">Configure your endpoint URLs for the events you want to receive notifications for</p>
          </div>

          <!-- Events Table -->
          <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table class="w-full">
              <thead class="bg-gray-100 border-b border-gray-200">
                <tr>
                  <th class="text-left py-3 px-4 text-sm font-medium text-gray-900">Event</th>
                  <th class="text-left py-3 px-4 text-sm font-medium text-gray-900">Description</th>
                  <th class="text-left py-3 px-4 text-sm font-medium text-gray-900">Your Endpoint URL</th>
                  <th class="text-left py-3 px-4 text-sm font-medium text-gray-900">Status</th>
                  <th class="text-left py-3 px-4 text-sm font-medium text-gray-900">Last Test</th>
                  <th class="text-center py-3 px-4 text-sm font-medium text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody>
                <template
                  v-for="eventEndpoint in eventEndpoints"
                  :key="eventEndpoint.eventKey"
                >
                  <!-- Main Event Row -->
                  <tr class="border-b border-gray-100 hover:bg-gray-50 transition-colors">
        
            <td class="py-3 px-4">
                      <div>
                        <span class="text-sm font-medium text-gray-900">{{ eventEndpoint.eventName }}</span>
                        <div class="text-xs text-gray-500">{{ eventEndpoint.eventKey }}</div>
                      </div>
                    </td>
                    <td class="py-3 px-4">
                      <span class="text-sm text-gray-600">{{ eventEndpoint.eventDescription }}</span>
                    </td>
                    <td class="py-3 px-4">
                      <div v-if="eventEndpoint.endpointUrl">
                        <div v-if="eventEndpoint.endpointUrl === '/api/webhook-test'" class="flex items-center gap-2">
                          <i class="fas fa-server text-blue-500"></i>
                          <span class="text-sm text-blue-600 font-medium">Local Webhook Receiver (Default)</span>
                        </div>
                        <div v-else>
                          <code class="text-sm text-gray-700 bg-gray-100 px-2 py-1 rounded">{{ eventEndpoint.endpointUrl }}</code>
                        </div>
                      </div>
                      <div v-else>
                        <span class="text-sm text-gray-400 italic">No endpoint configured</span>
                      </div>
                    </td>
                    <td class="py-3 px-4">
                      <span
                        class="px-2 py-1 text-xs rounded-full"
                        :class="eventEndpoint.isActive && eventEndpoint.endpointUrl 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'"
                      >
                        {{ eventEndpoint.isActive && eventEndpoint.endpointUrl ? 'Active' : 'Inactive' }}
                      </span>
                    </td>
                    <td class="py-3 px-4">
                      <div v-if="eventEndpoint.lastTested" class="text-sm">
                        <span
                          class="px-2 py-1 text-xs rounded-full"
                          :class="{
                            'bg-green-100 text-green-800': eventEndpoint.lastTestStatus === 'success',
                            'bg-red-100 text-red-800': eventEndpoint.lastTestStatus === 'failed' || eventEndpoint.lastTestStatus === 'error'
                          }"
                        >
                          {{ eventEndpoint.lastTestStatus }}
                        </span>
                        <div class="text-xs text-gray-500 mt-1">
                          {{ new Date(eventEndpoint.lastTested).toLocaleString() }}
                        </div>
                      </div>
                      <span v-else class="text-sm text-gray-400">Never tested</span>
                    </td>
                    <td class="py-3 px-4 text-center">
                      <EventRowActions
                        :event-endpoint="eventEndpoint"
                        :is-testing-event="isTestingEvent"
                        :testing-endpoint="testingEndpoint"
                        :expanded-log-rows="expandedLogRows"
                        @edit-endpoint="startEditEventEndpoint"
                        @test-endpoint="testEventEndpoint"
                        @toggle-logs="toggleInlineLogs"
                        @show-logs="showEventLogs"
                        @toggle-status="toggleEventStatus"
                        @switch-local="switchToLocalReceiver"
                        @remove-endpoint="removeEventEndpoint"
                        @open-response-builder="openResponseBuilder"
                      />
                    </td>
                  </tr>
                  
                  <!-- Expandable Logs Row -->
                  <tr v-if="expandedLogRows.has(eventEndpoint.eventKey)" class="bg-gray-50">
                    <td colspan="6" class="p-0">
                      <div class="px-4 py-4 border-t border-gray-200">
                        <div class="flex items-center justify-between mb-3">
                          <h5 class="text-sm font-medium text-gray-900">Recent Webhook Logs</h5>
                          <button
                            @click="showEventLogs(eventEndpoint)"
                            class="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                          >
                            <i class="fas fa-external-link-alt"></i>
                            View All Logs
                          </button>
                        </div>
                        
                        <div class="space-y-2">
                          <div v-for="log in getInlineLogsForEvent(eventEndpoint.eventKey).slice(0, 3)" :key="log.id">
                            <!-- Log Summary Row -->
                            <div class="bg-white rounded-lg border border-gray-200">
                              <div class="flex items-center justify-between p-3 text-sm">
                                <div class="flex items-center gap-3">
                                  <span
                                    class="px-2 py-1 text-xs rounded-full"
                                    :class="log.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                                  >
                                    {{ log.status }}
                                  </span>
                                  <span class="text-gray-600">{{ new Date(log.timestamp).toLocaleString() }}</span>
                                </div>
                                <div class="flex items-center gap-2">
                                  <div class="flex items-center gap-2 text-xs text-gray-500">
                                    <span>{{ log.responseTime }}ms</span>
                                    <span class="px-2 py-1 rounded" :class="log.statusCode === 200 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                                      {{ log.statusCode }}
                                    </span>
                                  </div>
                                  <button
                                    @click="toggleIndividualLog(log.id)"
                                    class="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                                    :title="expandedIndividualLogs.has(log.id) ? 'Hide details' : 'Show details'"
                                  >
                                    <i :class="expandedIndividualLogs.has(log.id) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="text-xs"></i>
                                  </button>
                                  <button
                                    @click="showLogDetail(log)"
                                    class="p-1 text-blue-500 hover:text-blue-700 rounded transition-colors"
                                    title="View in popup"
                                  >
                                    <i class="fas fa-external-link-alt text-xs"></i>
                                  </button>
                                </div>
                              </div>
                              
                              <!-- Expanded Log Details -->
                              <div v-if="expandedIndividualLogs.has(log.id)" class="border-t border-gray-200 p-3 bg-gray-50">
                                <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 text-xs">
                                  <!-- Payload Sent -->
                                  <div>
                                    <h6 class="font-medium text-gray-900 mb-2 flex items-center">
                                      <i class="fas fa-arrow-right text-blue-500 mr-2"></i>
                                      Payload Sent
                                    </h6>
                                    <pre class="bg-white border border-gray-200 rounded p-2 overflow-x-auto text-gray-700">{{ JSON.stringify(log.payload, null, 2) }}</pre>
                                  </div>
                                  
                                  <!-- Response Received -->
                                  <div>
                                    <h6 class="font-medium text-gray-900 mb-2 flex items-center">
                                      <i :class="log.status === 'success' ? 'fas fa-arrow-left text-green-500' : 'fas fa-exclamation-triangle text-red-500'" class="mr-2"></i>
                                      Response Received
                                    </h6>
                                    <div class="space-y-2">
                                      <div class="bg-white border border-gray-200 rounded p-2">
                                        <div class="font-medium text-gray-700 mb-1">Headers:</div>
                                        <pre class="text-gray-600">{{ JSON.stringify(log.response.headers, null, 2) }}</pre>
                                      </div>
                                      <div class="bg-white border border-gray-200 rounded p-2">
                                        <div class="font-medium text-gray-700 mb-1">Body:</div>
                                        <pre class="text-gray-600">{{ log.response.body }}</pre>
                                      </div>
                                      <div v-if="log.response.error" class="bg-red-50 border border-red-200 rounded p-2">
                                        <div class="font-medium text-red-700 mb-1">Error:</div>
                                        <pre class="text-red-600">{{ log.response.error }}</pre>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <div v-if="getInlineLogsForEvent(eventEndpoint.eventKey).length === 0" class="text-center py-4 text-gray-500 text-sm">
                            No logs available for this event
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <!-- Documentation Link -->
          <!-- <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <i class="fas fa-book text-blue-500 mr-2"></i>
                <span class="text-sm text-blue-800">Need help implementing webhooks?</span>
              </div>
              <a 
                href="https://docs.oddscomposer.io/webhooks" 
                target="_blank"
                class="text-pink-600 hover:text-pink-700 text-sm font-medium flex items-center gap-1"
              >
                <i class="fas fa-external-link-alt"></i>
                View Documentation
              </a>
            </div>
          </div> -->
        </div>

        <!-- RESTful API Tab -->
        <div v-if="activeTab === 'restful'" class="space-y-6">
          <!-- Header with Add Button -->
          <div class="flex items-center justify-between">
            <div>
              <h4 class="text-lg font-medium text-gray-900">RESTful API Endpoints</h4>
              <p class="text-sm text-gray-600 mt-1">Test and manage your RESTful API endpoints</p>
            </div>
            <button
              @click="showAddRestEndpointForm = true"
              class="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
            >
              <i class="fas fa-plus"></i>
              Add Custom Endpoint
            </button>
          </div>

          <!-- Quick Add Predefined Endpoints -->
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h5 class="text-lg font-medium text-gray-900 mb-4">Quick Add API Endpoints</h5>
            <p class="text-sm text-gray-600 mb-4">
              Add common OddsComposer API endpoints for quick testing. Click any endpoint to add it to your test collection.
            </p>
            
            <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table class="w-full">
                <thead class="bg-gray-100 border-b border-gray-200">
                  <tr>
                    <th class="text-left py-3 px-4 text-sm font-medium text-gray-900">Endpoint Name</th>
                    <th class="text-left py-3 px-4 text-sm font-medium text-gray-900">Method</th>
                    <th class="text-left py-3 px-4 text-sm font-medium text-gray-900">URL</th>
                    <th class="text-left py-3 px-4 text-sm font-medium text-gray-900">Category</th>
                    <th class="text-left py-3 px-4 text-sm font-medium text-gray-900">Description</th>
                    <th class="text-center py-3 px-4 text-sm font-medium text-gray-900">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="endpoint in predefinedEndpoints"
                    :key="endpoint.name"
                    class="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td class="py-3 px-4">
                      <span class="text-sm font-medium text-gray-900">{{ endpoint.name }}</span>
                    </td>
                    <td class="py-3 px-4">
                      <span class="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                        {{ endpoint.method }}
                      </span>
                    </td>
                    <td class="py-3 px-4">
                      <code class="text-sm text-gray-700 bg-gray-100 px-2 py-1 rounded">{{ endpoint.url }}</code>
                    </td>
                    <td class="py-3 px-4">
                      <span class="text-sm text-gray-600">{{ endpoint.category }}</span>
                    </td>
                    <td class="py-3 px-4">
                      <span class="text-sm text-gray-500">{{ endpoint.description }}</span>
                    </td>
                    <td class="py-3 px-4 text-center">
                      <button
                        @click="addPredefinedEndpoint(endpoint)"
                        class="bg-pink-600 hover:bg-pink-700 text-white px-3 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1 mx-auto"
                      >
                        <i class="fas fa-plus"></i>
                        Add
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Add REST Endpoint Form -->
          <div v-if="showAddRestEndpointForm" class="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h5 class="text-lg font-medium text-gray-900 mb-4">Add New REST Endpoint</h5>
            
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Endpoint Name</label>
                <input
                  v-model="newRestEndpoint.name"
                  type="text"
                  placeholder="My API Endpoint"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">API URL</label>
                <input
                  v-model="newRestEndpoint.url"
                  type="url"
                  placeholder="https://api.example.com/endpoint"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">HTTP Method</label>
                <select
                  v-model="newRestEndpoint.method"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                >
                  <option v-for="method in availableRestMethods" :key="method" :value="method">
                    {{ method }}
                  </option>
                </select>
              </div>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">API Version</label>
                <select
                  v-model="newRestEndpoint.version"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                >
                  <option v-for="version in apiVersions" :key="version.key" :value="version.key">
                    {{ version.name }}
                  </option>
                </select>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Custom Headers (JSON)</label>
                <input
                  v-model="newRestEndpoint.headers"
                  type="text"
                  placeholder='{"Authorization": "Bearer token"}'
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                />
              </div>
            </div>
            
            <div class="mt-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">Description (Optional)</label>
              <textarea
                v-model="newRestEndpoint.description"
                placeholder="Describe what this endpoint is used for..."
                rows="2"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              ></textarea>
            </div>
            
            <div v-if="['POST', 'PUT', 'PATCH'].includes(newRestEndpoint.method)" class="mt-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">Request Body (JSON)</label>
              <textarea
                v-model="newRestEndpoint.body"
                placeholder='{"key": "value"}'
                rows="4"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 font-mono text-sm"
              ></textarea>
            </div>
            
            <div class="flex items-center justify-end gap-3 mt-6">
              <button
                @click="showAddRestEndpointForm = false"
                class="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                @click="addRestEndpoint"
                class="px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Add Endpoint
              </button>
            </div>
          </div>

          <!-- REST Endpoints List -->
          <div v-if="restEndpoints.length > 0" class="space-y-4">
            <div
              v-for="endpoint in restEndpoints"
              :key="endpoint.id"
              class="bg-white border border-gray-200 rounded-lg p-6"
            >
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <div class="flex items-center gap-3 mb-2">
                    <h6 class="text-lg font-medium text-gray-900">{{ endpoint.name }}</h6>
                    <span
                      class="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800"
                    >
                      {{ endpoint.version || 'v1' }}
                    </span>
                    <span
                      class="px-2 py-1 text-xs rounded-full"
                      :class="{
                        'bg-green-100 text-green-800': endpoint.method === 'GET',
                        'bg-blue-100 text-blue-800': endpoint.method === 'POST',
                        'bg-yellow-100 text-yellow-800': endpoint.method === 'PUT' || endpoint.method === 'PATCH',
                        'bg-red-100 text-red-800': endpoint.method === 'DELETE'
                      }"
                    >
                      {{ endpoint.method }}
                    </span>
                    <span
                      class="px-2 py-1 text-xs rounded-full"
                      :class="endpoint.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'"
                    >
                      {{ endpoint.isActive ? 'Active' : 'Disabled' }}
                    </span>
                    <span
                      v-if="endpoint.lastTestStatus"
                      class="px-2 py-1 text-xs rounded-full"
                      :class="{
                        'bg-green-100 text-green-800': endpoint.lastTestStatus === 'success',
                        'bg-red-100 text-red-800': endpoint.lastTestStatus === 'failed' || endpoint.lastTestStatus === 'error'
                      }"
                    >
                      Last test: {{ endpoint.lastTestStatus }}
                    </span>
                  </div>
                  
                  <p class="text-sm text-gray-600 mb-2">{{ endpoint.url }}</p>
                  
                  <p v-if="endpoint.description" class="text-sm text-gray-500 mb-3">{{ endpoint.description }}</p>
                  
                  <div class="flex items-center gap-4 text-xs text-gray-500">
                    <span>Created: {{ new Date(endpoint.createdAt).toLocaleDateString() }}</span>
                    <span v-if="endpoint.lastTested">
                      Last tested: {{ new Date(endpoint.lastTested).toLocaleString() }}
                    </span>
                  </div>
                  
                  <div v-if="endpoint.lastTestResponse" class="mt-3 p-3 bg-gray-50 rounded border">
                    <h6 class="text-sm font-medium text-gray-700 mb-2">Last Test Response:</h6>
                    <div class="text-xs text-gray-600">
                      <div><strong>Status:</strong> {{ endpoint.lastTestResponse.status }} {{ endpoint.lastTestResponse.statusText }}</div>
                      <div v-if="endpoint.lastTestResponse.body" class="mt-2">
                        <strong>Response Body:</strong>
                        <pre class="mt-1 p-2 bg-white rounded border text-xs overflow-x-auto">{{ endpoint.lastTestResponse.body }}</pre>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div class="flex items-center gap-2 ml-4">
                  <button
                    @click="toggleRestEndpointStatus(endpoint.id)"
                    class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    :title="endpoint.isActive ? 'Disable endpoint' : 'Enable endpoint'"
                  >
                    <i :class="endpoint.isActive ? 'fas fa-pause' : 'fas fa-play'"></i>
                  </button>
                  
                  <button
                    @click="testRestEndpoint(endpoint)"
                    :disabled="isTestingRestEndpoint || !endpoint.isActive"
                    class="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Test REST endpoint"
                  >
                    <i v-if="testingRestEndpoint?.id === endpoint.id" class="fas fa-spinner fa-spin"></i>
                    <i v-else class="fas fa-paper-plane"></i>
                  </button>
                  
                  <button
                    @click="deleteRestEndpoint(endpoint.id)"
                    class="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete endpoint"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else class="text-center py-12 bg-gray-50 rounded-lg">
            <div class="text-gray-400 mb-4">
              <i class="fas fa-code text-4xl"></i>
            </div>
            <h5 class="text-lg font-medium text-gray-900 mb-2">No REST endpoints</h5>
            <p class="text-gray-600 mb-4">Add your first REST endpoint to start testing API integrations.</p>
            <button
              @click="showAddRestEndpointForm = true"
              class="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Add Your First Endpoint
            </button>
          </div>
        </div>

        <!-- Settings Tab -->
        <div v-if="activeTab === 'settings'" class="space-y-6">
          <div class="bg-white border border-gray-200 rounded-lg p-6">
            <h4 class="text-lg font-medium text-gray-900 mb-4">API Settings</h4>
            
            <div class="space-y-4">
              <div class="flex items-center justify-between">
                <div>
                  <h5 class="text-sm font-medium text-gray-900">Rate Limiting</h5>
                  <p class="text-sm text-gray-500">Configure API rate limits for your account</p>
                </div>
                <button class="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                  Configure
                </button>
              </div>

              <div class="border-t border-gray-200 pt-4">
                <div class="flex items-center justify-between">
                  <div>
                    <h5 class="text-sm font-medium text-gray-900">Webhook Settings</h5>
                    <p class="text-sm text-gray-500">Manage webhook endpoints and notifications</p>
                  </div>
                  <button class="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                    Manage
                  </button>
                </div>
              </div>

              <div class="border-t border-gray-200 pt-4">
                <div class="flex items-center justify-between">
                  <div>
                    <h5 class="text-sm font-medium text-gray-900">API Version</h5>
                    <p class="text-sm text-gray-500">Current version: v1.0</p>
                  </div>
                  <button class="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                    View Changelog
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div class="flex items-start">
              <div class="flex-shrink-0">
                <i class="fas fa-exclamation-triangle text-yellow-500 text-xl"></i>
              </div>
              <div class="ml-3">
                <h3 class="text-lg font-medium text-yellow-900">Important Notice</h3>
                <p class="mt-1 text-sm text-yellow-700">
                  API settings changes may take up to 5 minutes to take effect. Please test your integration after making changes.
                </p>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Edit Event Endpoint Popup -->
  <div v-if="showEditForm" 
       class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10000] flex items-center justify-center"
       @click.self="cancelEdit"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-md m-4">
      <div class="p-6">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-lg font-semibold text-gray-900">Configure Event Endpoint</h3>
            <p class="text-sm text-gray-600">{{ editingEvent?.eventName }}</p>
          </div>
          <button @click="cancelEdit" class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="space-y-4">
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div class="flex items-start">
              <i class="fas fa-info-circle text-blue-500 mt-0.5 mr-2"></i>
              <div class="text-sm text-blue-800">
                <strong>Event:</strong> {{ editingEvent?.eventKey }}<br>
                <strong>Description:</strong> {{ editingEvent?.eventDescription }}
              </div>
            </div>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Your Endpoint URL</label>
            <input
              v-model="editEventEndpoint.endpointUrl"
              type="url"
              placeholder="https://your-domain.com/webhooks/oddscomposer"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            />
            <p class="text-xs text-gray-500 mt-1">
              We'll send POST requests to this URL when the event occurs.
            </p>
          </div>
        </div>

        <div class="flex items-center justify-end gap-3 mt-6">
          <button
            @click="cancelEdit"
            class="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            @click="saveEventEndpoint"
            class="px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            {{ editingEvent?.endpointUrl ? 'Update Endpoint' : 'Add Endpoint' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Log Viewer Popup -->
  <div v-if="showLogViewer" 
       class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10000] flex items-center justify-center"
       @click.self="closeLogViewer"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-4xl m-4 max-h-[90vh] flex flex-col">
      <div class="p-6 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <div>
            <h3 class="text-lg font-semibold text-gray-900">Webhook Logs</h3>
            <p class="text-sm text-gray-600 mt-1">{{ viewingEventLogs?.eventName }} - {{ viewingEventLogs?.endpointUrl }}</p>
          </div>
          <button @click="closeLogViewer" class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto p-6">
        <div class="space-y-4">
          <div
            v-for="log in webhookLogs"
            :key="log.id"
            class="bg-gray-50 border border-gray-200 rounded-lg p-4"
          >
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center gap-3">
                <span
                  class="px-2 py-1 text-xs rounded-full"
                  :class="log.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                >
                  {{ log.status }}
                </span>
                <span class="text-sm font-medium text-gray-900">{{ log.event }}</span>
                <span class="text-xs text-gray-500">{{ new Date(log.timestamp).toLocaleString() }}</span>
              </div>
              <div class="flex items-center gap-2 text-xs text-gray-500">
                <span>{{ log.responseTime }}ms</span>
                <span>{{ log.statusCode }}</span>
              </div>
            </div>
            
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <h4 class="text-sm font-medium text-gray-700 mb-2">Payload</h4>
                <pre class="text-xs text-gray-600 bg-white border border-gray-200 rounded p-2 overflow-x-auto">{{ JSON.stringify(log.payload, null, 2) }}</pre>
              </div>
              
              <div v-if="log.error">
                <h4 class="text-sm font-medium text-red-700 mb-2">Error</h4>
                <div class="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
                  {{ log.error }}
                </div>
              </div>
              <div v-else>
                <h4 class="text-sm font-medium text-green-700 mb-2">Response</h4>
                <div class="text-xs text-green-600 bg-green-50 border border-green-200 rounded p-2">
                  HTTP {{ log.statusCode }} - Success
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div v-if="webhookLogs.length === 0" class="text-center py-8">
          <div class="text-gray-400 mb-2">
            <i class="fas fa-inbox text-2xl"></i>
          </div>
          <p class="text-gray-500">No logs available for this endpoint</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Confirmation Modal -->
  <div v-if="showConfirmModal" 
       class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10001] flex items-center justify-center"
       @click.self="cancelConfirm"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-md m-4">
      <div class="p-6">
        <div class="flex items-start mb-4">
          <div 
            class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center mr-3"
            :class="{
              'bg-red-100': confirmModalData.type === 'danger',
              'bg-yellow-100': confirmModalData.type === 'warning',
              'bg-blue-100': confirmModalData.type === 'info'
            }"
          >
            <i 
              class="text-lg"
              :class="{
                'fas fa-exclamation-triangle text-red-600': confirmModalData.type === 'danger',
                'fas fa-exclamation-triangle text-yellow-600': confirmModalData.type === 'warning',
                'fas fa-info-circle text-blue-600': confirmModalData.type === 'info'
              }"
            ></i>
          </div>
          <div class="flex-1">
            <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ confirmModalData.title }}</h3>
            <p class="text-sm text-gray-600">{{ confirmModalData.message }}</p>
          </div>
        </div>

        <div class="flex items-center justify-end gap-3">
          <button
            @click="cancelConfirm"
            class="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium transition-colors"
          >
            {{ confirmModalData.cancelText }}
          </button>
          <button
            @click="confirmAction"
            class="px-4 py-2 text-white rounded-lg text-sm font-medium transition-colors"
            :class="{
              'bg-red-600 hover:bg-red-700': confirmModalData.type === 'danger',
              'bg-yellow-600 hover:bg-yellow-700': confirmModalData.type === 'warning',
              'bg-blue-600 hover:bg-blue-700': confirmModalData.type === 'info'
            }"
          >
            {{ confirmModalData.confirmText }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Notification Toast -->
  <div v-if="showNotification" 
       class="fixed top-4 right-4 z-[10002] transform transition-all duration-300"
  >
    <div 
      class="bg-white rounded-lg shadow-lg border-l-4 p-4 max-w-sm"
      :class="{
        'border-green-500': notificationData.type === 'success',
        'border-red-500': notificationData.type === 'error',
        'border-blue-500': notificationData.type === 'info'
      }"
    >
      <div class="flex items-start">
        <div class="flex-shrink-0">
          <i 
            class="text-lg mr-3"
            :class="{
              'fas fa-check-circle text-green-500': notificationData.type === 'success',
              'fas fa-exclamation-circle text-red-500': notificationData.type === 'error',
              'fas fa-info-circle text-blue-500': notificationData.type === 'info'
            }"
          ></i>
        </div>
        <div class="flex-1">
          <h4 class="text-sm font-semibold text-gray-900">{{ notificationData.title }}</h4>
          <p class="text-sm text-gray-600 mt-1">{{ notificationData.message }}</p>
        </div>
        <button @click="closeNotification" class="ml-2 text-gray-400 hover:text-gray-600">
          <i class="fas fa-times text-sm"></i>
        </button>
      </div>
    </div>
  </div>

  <!-- Log Detail Popup -->
  <div v-if="showLogDetailPopup" 
       class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10003] flex items-center justify-center"
       @click.self="closeLogDetail"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-4xl m-4 max-h-[90vh] flex flex-col">
      <div class="p-6 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <div>
            <h3 class="text-lg font-semibold text-gray-900">Webhook Log Details</h3>
            <div class="flex items-center gap-3 mt-1">
              <span
                class="px-2 py-1 text-xs rounded-full"
                :class="selectedLogDetail?.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
              >
                {{ selectedLogDetail?.status }}
              </span>
              <span class="text-sm text-gray-600">{{ selectedLogDetail ? new Date(selectedLogDetail.timestamp).toLocaleString() : '' }}</span>
              <span class="text-sm text-gray-500">{{ selectedLogDetail?.responseTime }}ms</span>
              <span class="px-2 py-1 text-xs rounded" :class="selectedLogDetail?.statusCode === 200 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                {{ selectedLogDetail?.statusCode }}
              </span>
            </div>
          </div>
          <button @click="closeLogDetail" class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto p-6">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Request Details -->
          <div class="space-y-4">
            <div>
              <h4 class="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <i class="fas fa-arrow-right text-blue-500 mr-2"></i>
                Request Sent
              </h4>
              
              <div class="space-y-3">
                <div class="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h5 class="text-sm font-medium text-gray-700 mb-2">HTTP Headers</h5>
                  <pre class="text-xs text-gray-600 bg-white border border-gray-200 rounded p-2 overflow-x-auto">{{
                    JSON.stringify({
                      'Content-Type': 'application/json',
                      'User-Agent': 'OddsComposer-Webhook/1.0',
                      'X-Webhook-Event': selectedLogDetail?.payload?.event,
                      'X-Webhook-Signature': selectedLogDetail?.payload?.signature
                    }, null, 2)
                  }}</pre>
                </div>
                
                <div class="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h5 class="text-sm font-medium text-gray-700 mb-2">Payload</h5>
                  <pre class="text-xs text-gray-600 bg-white border border-gray-200 rounded p-2 overflow-x-auto">{{ selectedLogDetail ? JSON.stringify(selectedLogDetail.payload, null, 2) : '' }}</pre>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Response Details -->
          <div class="space-y-4">
            <div>
              <h4 class="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <i :class="selectedLogDetail?.status === 'success' ? 'fas fa-arrow-left text-green-500' : 'fas fa-exclamation-triangle text-red-500'" class="mr-2"></i>
                Response Received
              </h4>
              
              <div class="space-y-3">
                <div class="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h5 class="text-sm font-medium text-gray-700 mb-2">Status</h5>
                  <div class="flex items-center gap-2">
                    <span class="px-2 py-1 text-sm rounded" :class="selectedLogDetail?.statusCode === 200 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                      {{ selectedLogDetail?.statusCode }} {{ selectedLogDetail?.response?.statusText }}
                    </span>
                    <span class="text-sm text-gray-500">in {{ selectedLogDetail?.responseTime }}ms</span>
                  </div>
                </div>
                
                <div class="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h5 class="text-sm font-medium text-gray-700 mb-2">Response Headers</h5>
                  <pre class="text-xs text-gray-600 bg-white border border-gray-200 rounded p-2 overflow-x-auto">{{ selectedLogDetail ? JSON.stringify(selectedLogDetail.response.headers, null, 2) : '' }}</pre>
                </div>
                
                <div class="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h5 class="text-sm font-medium text-gray-700 mb-2">Response Body</h5>
                  <pre class="text-xs text-gray-600 bg-white border border-gray-200 rounded p-2 overflow-x-auto">{{ selectedLogDetail?.response?.body }}</pre>
                </div>
                
                <div v-if="selectedLogDetail?.response?.error" class="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h5 class="text-sm font-medium text-red-700 mb-2">Error Details</h5>
                  <pre class="text-xs text-red-600 bg-white border border-red-200 rounded p-2 overflow-x-auto">{{ selectedLogDetail.response.error }}</pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="p-6 border-t border-gray-200 bg-gray-50">
        <div class="flex items-center justify-end">
          <button
            @click="closeLogDetail"
            class="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Response Builder Modal -->
  <div v-if="showResponseBuilder" 
       class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10004] flex items-center justify-center"
       @click.self="closeResponseBuilder"
  >
    <div class="bg-white rounded-xl shadow-2xl w-[95vw] h-[95vh] m-4 flex flex-col">
      <div class="p-6 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <div>
            <h3 class="text-lg font-semibold text-gray-900">Response Builder</h3>
            <p class="text-sm text-gray-600 mt-1">Configure and test {{ buildingEventType }} webhook response</p>
          </div>
          <button @click="closeResponseBuilder" class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <div class="flex-1 overflow-hidden p-6">
        <div v-if="buildingEventType === 'fixture.created'" class="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
          <!-- Left Column: Configuration -->
          <div class="space-y-6 overflow-y-auto">
            <!-- Query Parameters -->
            <div class="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h4 class="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <i class="fas fa-sliders-h text-blue-500 mr-2"></i>
                Query Parameters
              </h4>
              
              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Fixture ID</label>
                  <input
                    v-model.number="responseBuilderConfig.fixtureId"
                    type="number"
                    min="1"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    placeholder="143"
                  />
                  <p class="text-xs text-gray-500 mt-1">Fixture ID to use for testing (default: 143)</p>
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Webhook Version</label>
                  <select
                    v-model="responseBuilderConfig.webhookVersion"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  >
                    <option v-for="version in webhookVersions" :key="version.key" :value="version.key">
                      {{ version.name }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Root Fields Configuration -->
            <div class="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h4 class="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <i class="fas fa-list text-purple-500 mr-2"></i>
                Root Fields
              </h4>
              
              <div class="space-y-2 max-h-96 overflow-y-auto">
                <div 
                  v-for="field in [
                    { key: 'event', name: 'Event Type', description: 'Event identifier' },
                    { key: 'fixture_id', name: 'Fixture ID', description: 'Unique fixture identifier' },
                    { key: 'timestamp', name: 'Timestamp', description: 'Event occurrence time' },
                    { key: 'start_time', name: 'Game Start Time', description: 'Fixture start time' },
                    { key: 'league_id', name: 'League ID', description: 'League identifier' },
                    { key: 'home_team_id', name: 'Home Team ID', description: 'Home team identifier' },
                    { key: 'away_team_id', name: 'Away Team ID', description: 'Away team identifier' },
                    { key: 'home_team_name', name: 'Home Team Name', description: 'Home team name' },
                    { key: 'away_team_name', name: 'Away Team Name', description: 'Away team name' },
                    { key: 'league_name', name: 'League Name', description: 'League name' }
                  ]" 
                  :key="field.key"
                  class="bg-white border border-gray-200 rounded-lg"
                >
                  <div class="flex items-center justify-between p-3">
                    <div class="flex-1">
                      <div class="font-medium text-gray-900 text-sm">{{ field.name }}</div>
                      <div class="text-xs text-gray-500">{{ field.description }}</div>
                    </div>
                    <label class="flex items-center ml-3">
                      <input
                        :id="`root-field-${field.key}`"
                        :checked="getFieldConfig(field.key)"
                        @change="setFieldConfig(field.key, $event.target.checked)"
                        type="checkbox"
                        class="w-4 h-4 text-pink-600 bg-gray-100 border-gray-300 rounded focus:ring-pink-500"
                      />
                    </label>
                  </div>
                  <!-- Field Preview -->
                  <div v-if="getFieldConfig(field.key)" class="px-3 pb-3 border-t border-gray-100">
                    <div class="text-xs text-gray-400 mb-1 mt-2">Response preview:</div>
                    <code class="text-xs bg-gray-50 text-gray-700 border border-gray-200 px-2 py-1 rounded block overflow-x-auto font-mono">
                      <span class="text-blue-400">"{{ field.key }}"</span><span class="text-gray-500">: </span><span v-html="getFormattedFieldPreview(field.key)"></span>
                    </code>
                  </div>
                </div>
              </div>
            </div>

            <!-- Array Fields Configuration -->
            <div class="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h4 class="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <i class="fas fa-database text-green-500 mr-2"></i>
                Array Fields
              </h4>
              
              <div class="space-y-6">
                <!-- Player Lines Section -->
                <div class="bg-white border border-gray-200 rounded-lg">
                  <div class="flex items-center justify-between p-4 border-b border-gray-100">
                    <div class="flex-1">
                      <div class="font-medium text-gray-900">Player Lines</div>
                      <div class="text-sm text-gray-500">Array of player betting lines</div>
                    </div>
                    <label class="flex items-center">
                      <input
                        id="array-field-player_lines"
                        :checked="getFieldConfig('player_lines')"
                        @change="setFieldConfig('player_lines', $event.target.checked)"
                        type="checkbox"
                        class="w-4 h-4 text-pink-600 bg-gray-100 border-gray-300 rounded focus:ring-pink-500"
                      />
                      <span class="ml-2 text-sm text-gray-700">
                        {{ getFieldConfig('player_lines') ? 'Include' : 'Exclude' }}
                      </span>
                    </label>
                  </div>
                  
                  <!-- Player Lines Field Selection -->
                  <div v-if="getFieldConfig('player_lines')" class="p-4">
                    <div class="grid grid-cols-2 gap-2">
                      <div v-for="(enabled, fieldKey) in responseBuilderConfig.playerLinesFields" :key="fieldKey" class="flex items-center">
                        <input
                          :id="`player-line-field-${fieldKey}`"
                          :checked="enabled"
                          @change="setNestedFieldConfig('playerLinesFields', fieldKey, $event.target.checked)"
                          type="checkbox"
                          class="w-3 h-3 text-pink-600 bg-gray-100 border-gray-300 rounded focus:ring-pink-500"
                        />
                        <label :for="`player-line-field-${fieldKey}`" class="ml-2 text-xs text-gray-700 font-mono">{{ fieldKey }}</label>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Specials Section -->
                <div class="bg-white border border-gray-200 rounded-lg">
                  <div class="flex items-center justify-between p-4 border-b border-gray-100">
                    <div class="flex-1">
                      <div class="font-medium text-gray-900">Special Markets</div>
                      <div class="text-sm text-gray-500">Special betting markets</div>
                    </div>
                    <label class="flex items-center">
                      <input
                        id="array-field-specials"
                        :checked="getFieldConfig('specials')"
                        @change="setFieldConfig('specials', $event.target.checked)"
                        type="checkbox"
                        class="w-4 h-4 text-pink-600 bg-gray-100 border-gray-300 rounded focus:ring-pink-500"
                      />
                      <span class="ml-2 text-sm text-gray-700">
                        {{ getFieldConfig('specials') ? 'Include' : 'Exclude' }}
                      </span>
                    </label>
                  </div>
                  
                  <!-- Specials Field Selection -->
                  <div v-if="getFieldConfig('specials')" class="p-4">
                    <div class="grid grid-cols-2 gap-2">
                      <div v-for="(enabled, fieldKey) in responseBuilderConfig.specialsFields" :key="fieldKey" class="flex items-center">
                        <input
                          :id="`special-field-${fieldKey}`"
                          :checked="enabled"
                          @change="setNestedFieldConfig('specialsFields', fieldKey, $event.target.checked)"
                          type="checkbox"
                          class="w-3 h-3 text-pink-600 bg-gray-100 border-gray-300 rounded focus:ring-pink-500"
                        />
                        <label :for="`special-field-${fieldKey}`" class="ml-2 text-xs text-gray-700 font-mono">{{ fieldKey }}</label>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Other Array Fields -->
                <div 
                  v-for="field in [
                    { key: 'balanced_lines', name: 'Balanced Lines', description: 'Balanced betting lines' },
                    { key: 'milestones', name: 'Milestones', description: 'Milestone betting options' }
                  ]" 
                  :key="field.key"
                  class="bg-white border border-gray-200 rounded-lg"
                >
                  <div class="flex items-center justify-between p-4">
                    <div class="flex-1">
                      <div class="font-medium text-gray-900">{{ field.name }}</div>
                      <div class="text-sm text-gray-500">{{ field.description }}</div>
                    </div>
                    <label class="flex items-center">
                      <input
                        :id="`array-field-${field.key}`"
                        :checked="getFieldConfig(field.key)"
                        @change="setFieldConfig(field.key, $event.target.checked)"
                        type="checkbox"
                        class="w-4 h-4 text-pink-600 bg-gray-100 border-gray-300 rounded focus:ring-pink-500"
                      />
                      <span class="ml-2 text-sm text-gray-700">
                        {{ getFieldConfig(field.key) ? 'Include' : 'Exclude' }}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Column: Response Preview -->
          <div class="bg-white border border-gray-200 rounded-lg p-6 flex flex-col">
            <div class="flex items-center justify-between mb-4">
              <h4 class="text-lg font-medium text-gray-900 flex items-center">
                <i class="fas fa-eye text-purple-500 mr-2"></i>
                Response Preview
              </h4>
            </div>
            
            <div class="flex-1 bg-gray-900 rounded-lg p-4 overflow-hidden flex flex-col">
              <pre class="text-sm text-gray-300 overflow-auto flex-1" v-html="formatJsonWithColors(responsePreview)"></pre>
            </div>
          </div>
        </div>

        <!-- Unsupported Event Type -->
        <div v-else class="flex items-center justify-center h-full">
          <div class="text-center">
            <div class="text-gray-400 mb-4">
              <i class="fas fa-exclamation-triangle text-4xl"></i>
            </div>
            <h4 class="text-lg font-medium text-gray-900 mb-2">Response Builder Not Available</h4>
            <p class="text-gray-600">Response builder is currently only available for fixture.created events.</p>
          </div>
        </div>
      </div>

      <div class="p-6 border-t border-gray-200 bg-gray-50">
        <div class="flex items-center justify-end gap-3">
          <button
            @click="closeResponseBuilder"
            class="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium transition-colors"
          >
            Close
          </button>
          <button
            v-if="buildingEventType === 'fixture.created'"
            @click="testResponseBuilder(eventEndpoints.find(e => e.eventKey === buildingEventType))"
            class="px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
          >
            <i class="fas fa-paper-plane text-xs"></i>
            Test with Builder Config
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Webhook Success Popup -->
  <WebhookSuccessPopup 
    :show="showWebhookSuccessPopup"
    :response-data="webhookSuccessData"
    @close="closeWebhookSuccessPopup"
  />
</template>
